// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  getAdditionalUserInfo
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase/config';
import {
  trackLogin,
  trackSignUp,
  trackBetaAccessGranted,
  trackBetaAccessDenied,
  setAnalyticsUserId,
  setAnalyticsUserProperties
} from '../services/analyticsService';
import { consumeSignupSource } from '../utils/signupSource';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Beta access control states
  const [betaStatus, setBetaStatus] = useState(null); // 'approved' | 'pending' | 'rejected' | 'not_registered' | null
  const [betaLoading, setBetaLoading] = useState(true);

  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Check beta access status when user changes
  useEffect(() => {
    const checkBetaAccess = async () => {
      if (!user) {
        setBetaStatus(null);
        setBetaLoading(false);
        return;
      }

      setBetaLoading(true);
      try {
        const email = user.email.toLowerCase();
        const betaDocRef = doc(db, 'betaUsers', email);
        const betaDoc = await getDoc(betaDocRef);

        if (betaDoc.exists()) {
          const data = betaDoc.data();
          setBetaStatus(data.status);

          // Update last login timestamp for approved users
          if (data.status === 'approved') {
            await updateDoc(betaDocRef, {
              lastLoginAt: serverTimestamp(),
            });
            // Track beta access granted
            trackBetaAccessGranted(data.source || 'whitelist');
            setAnalyticsUserProperties({ beta_tester: true });
          } else {
            // Track beta access denied (pending, rejected, revoked)
            trackBetaAccessDenied(data.status);
            setAnalyticsUserProperties({ beta_tester: false });
          }
        } else {
          // User not in betaUsers collection - auto-approve for open beta
          // Create betaUsers entry in background (triggers welcome email via Cloud Function)
          setDoc(doc(db, 'betaUsers', email), {
            email: email,
            status: 'approved',
            source: 'open_beta_auto',
            approvedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }).catch(err => console.error('Failed to create betaUsers entry:', err));

          // Always approve immediately for open beta (non-blocking)
          setBetaStatus('approved');
          trackSignUp('google');
          trackBetaAccessGranted('open_beta_auto');
          setAnalyticsUserProperties({ beta_tester: true });
        }
      } catch (error) {
        console.error('Error checking beta access:', error);
        // On error, still approve for open beta (don't block users)
        setBetaStatus('approved');
      } finally {
        setBetaLoading(false);
      }
    };

    checkBetaAccess();
  }, [user]);

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Track login and set user ID for analytics
      trackLogin('google');
      setAnalyticsUserId(result.user.uid);

      // First-time signup: persist a user profile doc with acquisition source.
      // Uses Firebase's isNewUser flag from the OAuth result so this only fires once.
      const additional = getAdditionalUserInfo(result);
      if (additional?.isNewUser) {
        const source = consumeSignupSource();
        setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email || null,
          displayName: result.user.displayName || null,
          provider: 'google',
          createdAt: serverTimestamp(),
          signupSource: source || { referrer: null, landingPath: null },
        }, { merge: true }).catch((err) =>
          console.error('Failed to write users/{uid} on signup:', err)
        );
      }

      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Email/password sign-in for emulator mode (Playwright testing)
  const loginWithEmail = async (email, password) => {
    if (process.env.REACT_APP_USE_EMULATORS !== 'true') {
      throw new Error('Email login is only available in emulator mode');
    }
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      trackLogin('email_emulator');
      setAnalyticsUserId(result.user.uid);
      return result.user;
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  // Expose login on window for Playwright in emulator mode
  useEffect(() => {
    if (process.env.REACT_APP_USE_EMULATORS === 'true') {
      window.__chaggerheart = {
        loginWithEmail: (email, password) =>
          signInWithEmailAndPassword(auth, email, password)
            .then(cred => ({ uid: cred.user.uid, email: cred.user.email })),
        logout: () => signOut(auth),
      };
    }
    return () => { delete window.__chaggerheart; };
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    betaStatus,
    betaLoading,
    loginWithGoogle,
    loginWithEmail,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
